import { Identity } from 'openfin/_v2/main';

import type { CreatePayload, NotificationInternal } from '../../../client/internal';
import type { NotificationSource } from '../../../client';
import { StoredNotification } from '../../model/StoredNotification';
import { getMarkdownTextContent } from '../../view/utils/Markdown';
import {
  NotificationOptions,
  TemplateMarkdown,
  TemplateCustom,
  TemplateNames,
} from '../../../client/templates';
import { isTemplate } from '../../model/templates/isTemplate';
import { validateTemplateData } from '../templates/validateTemplateData';
import { TemplateSchemaMap } from '../../model/templates/schemas';

/**
 * Hydrate notification options to create a StoredNotification.
 *
 * @param payload Notification options to hydrate.
 * @param source The source of the notification.
 * @param id Id of the hydrated StoredNotification
 */
export function hydrateNotification<T extends NotificationOptions = NotificationOptions>(
  payload: CreatePayload<T>,
  source: NotificationSource,
  id?: string
): StoredNotification<T> {
  payload.template ??= 'markdown';

  const schema = TemplateSchemaMap[payload.template];
  if (!schema) {
    throw new Error(`Unrecognized notification template name, must be one of:\
     ${Object.values(TemplateNames).join(',')}`);
  }

  const notification: NotificationInternal = (schema.validateSync(
    payload
  ) as unknown) as NotificationInternal;

  const storedNotificationId =
    id ??
    (source.type === 'desktop' ? encodeId(notification.id, source.identity) : notification.id);

  const bodyText = isTemplate<TemplateMarkdown>(notification, 'markdown')
    ? getMarkdownTextContent(notification.body)
    : '';

  if (isTemplate<TemplateCustom>(notification, 'custom')) {
    validateTemplateData(notification as TemplateCustom, !payload.indicator?.color);
  }

  return ({
    id: storedNotificationId,
    notification,
    source,
    bodyText,
    // The title will be set when reduced in the store
    title: '',
  } as unknown) as StoredNotification<T>;
}

/**
 * Encodes the Id which currently is the uuid:id
 * @param id Id of the notification..
 * @param source The sender of the notification.
 */
export function encodeId(id: string, source: Identity): string {
  return `${source.uuid}:${id}`;
}
